<template>
  <v-row no-gutters class="pt-5 fill-height">
    <v-col v-if="mapPanelDataFetched" class="pr-2">
      <card-title-row translation-path="sidebar.calibrationFilesGeneration.title" :has-buttons="false">
        <template #subtitle>
          <italic-subheader
            :straight-text="
              $i18n.t('sidebar.calibrationFilesGeneration.subtitle', {
                duration: settingsValues['DURATION'],
                step: $dayjs(settingsValues['HYDRAULIC TIMESTEP'], 'HH:mm').format('H'),
                start: settingsValues['START CLOCKTIME'],
              })
            "
            style="text-align: left"
          />
        </template>
      </card-title-row>
      <v-form ref="calibrationFileForm" v-model="isFormValid">
        <sidebar-row class="pt-10">
          <v-row no-gutters align="baseline">
            <v-col cols="4" class="text-body-2 text-left pr-4 align-self-stretch">
              {{ $i18n.t('sidebar.calibrationFilesGeneration.timeInterval') }}:
            </v-col>
            <v-col cols="1">
              <v-icon class="pr-1" color="rgba(0,0,0,0.7)">mdi-calendar</v-icon>
            </v-col>
            <v-col cols="7">
              <data-input
                v-model="timeInterval"
                :data-type="{ name: 'daterange' }"
                locale-date-format="YYYY-MM-DD"
                return-type="date"
                v-bind="{
                  rules: [v => !!v || $i18n.t('rules.required')],
                }"
              />
            </v-col>
          </v-row>
        </sidebar-row>
        <sidebar-row class="pt-4">
          <v-row no-gutters class="justify-space-between">
            <v-col cols="4" class="text-body-2 center-content text-left">
              {{ $i18n.t('sidebar.calibrationFilesGeneration.calculationsMethod') }}:
            </v-col>
            <v-col cols="7">
              <v-radio-group v-model="calculationsMethod" class="center-content" hide-details mandatory>
                <v-radio value="exact">
                  <template #label>
                    <div class="text-body-2 text-left label-dark">
                      {{ $i18n.t('sidebar.calibrationFilesGeneration.fullIntervalData') }}
                    </div>
                  </template>
                </v-radio>
                <v-radio value="average">
                  <template #label>
                    <div class="text-body-2 text-left label-dark">
                      {{ $i18n.t('sidebar.calibrationFilesGeneration.averageForInterval') }}
                    </div>
                  </template>
                </v-radio>
              </v-radio-group>
            </v-col>
          </v-row>
        </sidebar-row>
        <sidebar-row class="pt-7">
          <v-row no-gutters class="justify-space-between">
            <v-col cols="4" class="text-body-2 text-left center-content">
              {{ $i18n.t('sidebar.calibrationFilesGeneration.noDataFilterMethod') }}:
            </v-col>
            <v-col cols="7">
              <v-radio-group v-model="filterMethod" class="center-content" hide-details mandatory>
                <v-radio value="skip">
                  <template #label>
                    <div class="text-body-2 text-left label-dark">
                      {{ $i18n.t('sidebar.calibrationFilesGeneration.skipSeries') }}
                    </div>
                  </template>
                </v-radio>
                <v-radio value="error">
                  <template #label>
                    <div class="text-body-2 text-left label-dark">
                      {{ $i18n.t('sidebar.calibrationFilesGeneration.showErrorMessage') }}
                    </div>
                  </template>
                </v-radio>
              </v-radio-group>
            </v-col>
          </v-row>
        </sidebar-row>
        <sidebar-row class="pt-7">
          <v-checkbox v-model="averageData">
            <template #label>
              <div class="text-body-2 text-left label-dark">
                {{ $i18n.t('sidebar.calibrationFilesGeneration.averageData') }}
              </div>
            </template>
          </v-checkbox>
        </sidebar-row>
      </v-form>
      <sidebar-row class="mb-5">
        <v-row>
          <v-col>
            <card-title-row
              :has-icon-column="false"
              is-subtitle
              translation-path="sidebar.calibrationFilesGeneration.scadaAttributes"
              :amount="1"
              :is-plural="true"
            >
              <template #subtitle>
                <italic-subheader
                  style="text-align: left; max-width: 20vw"
                  translation-path="sidebar.calibrationFilesGeneration.scadaAttributesSubtitle"
                ></italic-subheader>
              </template>
            </card-title-row>
          </v-col>
        </v-row>
        <v-row class="py-2">
          <v-col>
            <data-input
              v-model="scadaDataLayer"
              @change="changeScadaDataLayer"
              hide-details
              :data-type="{ name: 'select' }"
              :label="$i18n.t('sidebar.calibrationFilesGeneration.layer')"
              item-text="name"
              item-value="layer_id"
              return-object
              :items="projectLayers"
            />
          </v-col>
          <v-col>
            <data-input
              v-model="scadaDataAttribute"
              @change="addScadaAttribute"
              hide-details
              :disabled="!scadaDataLayer"
              :data-type="{ name: 'select' }"
              item-text="label"
              item-value="attribute"
              return-object
              :label="$i18n.t('sidebar.calibrationFilesGeneration.attribute')"
              :items="scadaDataLayerAttributes"
            />
          </v-col>
        </v-row>
        <v-row v-if="scadaDataList.length > 0">
          <v-col class="chips-container">
            <v-chip
              v-for="(item, index) in scadaDataList"
              :key="item.layer_id"
              close
              close-icon="mdi-close-circle"
              @click:close="deleteScadaAttribute(index)"
            >
              <span class="text-left">
                <span class="mr-1">{{ item.layer.name }}:</span
                ><span class="font-weight-bold">{{ item.attribute.label }}</span>
              </span>
            </v-chip>
          </v-col>
        </v-row>
      </sidebar-row>
    </v-col>
    <v-col class="fill-height" v-else>
      <main-loader />
    </v-col>
  </v-row>
</template>

<script>
import SidebarRow from '@/components/SidebarRow';
import CardTitleRow from '@/components/CardTitleRow';
import ItalicSubheader from '@/components/ItalicSubheader';
import DataInput from '@/components/DataInput';
import MainLoader from '@/components/MainLoader';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

import { get, sync, call } from 'vuex-pathify';

export default {
  name: 'CalibrationFilesGeneration',
  mixins: [prepareAttributesFeatureCardMixin],
  components: {
    CardTitleRow,
    ItalicSubheader,
    SidebarRow,
    DataInput,
    MainLoader,
  },
  data: () => ({
    isFormValid: false,
    calculationsMethod: undefined,
    filterMethod: undefined,
    timeInterval: [],
    averageData: false,
    scadaDataLayer: null,
    scadaDataAttribute: null,
    scadaDataLayerAttributes: [],
    scadaDataList: [],
  }),
  computed: {
    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    stickyButtons: sync('sidebar/stickyButtons'),
    projectElements: get('layers/project@layers'),
    settingsValues: get('admin/settingsValues.epanet_options'),
    dataSources: get('layers/metadata'),
    mapPanelDataFetched: get('map/mapPanelDataFetched'),
    layers: get('layers/layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    allDataValid() {
      return this.isFormValid && this.scadaDataList.length > 0;
    },
  },
  watch: {
    allDataValid: {
      immediate: true,
      handler(nV) {
        this.isStickyButtonsValid = nV;
      },
    },
  },
  methods: {
    exportCalibrationFile: call('epanet/getCalibrationFile'),
    async generateCalibrationFile() {
      this.isStickyButtonsLoading = true;
      const payload = {
        average_to_model_duration: this.averageData,
        data_calculation: this.calculationsMethod,
        no_data: this.filterMethod,
        since: this.timeInterval[0],
        until: this.timeInterval.length > 1 ? this.timeInterval[1] : this.timeInterval[0],
        layers: this.scadaDataList.map(item => {
          return {
            layer_id: item.layer.layer_id,
            attribute: item.attribute.attribute,
          };
        }),
      };
      try {
        await this.exportCalibrationFile(payload);
      } finally {
        this.isStickyButtonsLoading = false;
      }
    },
    changeScadaDataLayer() {
      const dataSourceAttributes = this.dataSources[this.scadaDataLayer?.data_source_name]?.attributes_schema
        .attributes;
      const schemaForm = this.getForm(
        this.layers[this.scadaDataLayer.layer_id]?.form_schema.elements,
        dataSourceAttributes
      );
      const layerAttributes = this.$_flattenStructure(schemaForm, 'elements');
      this.scadaDataLayerAttributes = layerAttributes;
    },
    addScadaAttribute() {
      this.scadaDataList.push({
        layer: this.scadaDataLayer,
        attribute: this.scadaDataAttribute,
      });
      this.scadaDataLayerAttributes = [];
      this.scadaDataAttribute = null;
      this.scadaDataLayer = null;
    },
    deleteScadaAttribute(index) {
      let paramsList = [...this.scadaDataList];
      paramsList.splice(index, 1);
      this.scadaDataList = paramsList;
    },
  },
  mounted() {
    this.$root.$off('generateCalibrationFile');
    this.$root.$on('generateCalibrationFile', this.generateCalibrationFile);
    this.stickyButtons = [
      {
        action: 'generateCalibrationFile',
        translationPath: 'sidebar.calibrationFilesGeneration.download',
      },
    ];
    this.isStickyButtonsValid = false;
    this.isStickyButtonsLoading = false;
    this.isStickyButtonsVisible = true;
  },
  beforeDestroy() {
    this.isStickyButtonsVisible = false;
    this.stickyButtons = [];
  },
};
</script>

<style lang="scss" scoped>
.label-dark {
  color: #202124;
}
.center-content {
  display: flex;
  align-items: center;
}
.chips-container {
  white-space: normal;
  flex-wrap: wrap;
  max-width: 100%;
  display: flex;
  gap: 0.5rem;
}
::v-deep {
  .v-input--selection-controls {
    margin-top: 0;
    padding-top: 0;
  }
  .v-chip {
    overflow: initial !important;
    white-space: initial !important;
    height: auto !important;
    padding-top: 0.4rem;
    padding-bottom: 0.4rem;
  }
}
</style>
